import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import SmartDateInput from "@/components/SmartDateInput";
import { toast } from "sonner";
import { UploadSimple, User, Person, ArrowUUpLeft, Camera, Images, X as CloseIcon } from "@phosphor-icons/react";

const TAGS = [
  { key: "fronte", label: "Fronte", icon: User },
  { key: "profilo", label: "Profilo", icon: Person },
  { key: "dietro", label: "Dietro", icon: ArrowUUpLeft },
];

export default function PhotoUploader({ clientId, onUploaded }) {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [tag, setTag] = useState("fronte");
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  const pickFile = (e) => {
    const f = e.target.files?.[0] || null;
    setFile(f);
    if (f) {
      const reader = new FileReader();
      reader.onload = () => setPreview(reader.result);
      reader.readAsDataURL(f);
    } else {
      setPreview(null);
    }
  };

  const clearFile = () => {
    setFile(null);
    setPreview(null);
    if (cameraInputRef.current) cameraInputRef.current.value = "";
    if (galleryInputRef.current) galleryInputRef.current.value = "";
  };

  const doUpload = async (e) => {
    e.preventDefault();
    if (!file) return;
    setUploading(true);
    try {
      const { data: sig } = await api.get(`/cloudinary/signature?folder=rilievi/${clientId}`);
      const form = new FormData();
      form.append("file", file);
      form.append("api_key", sig.api_key);
      form.append("timestamp", sig.timestamp);
      form.append("signature", sig.signature);
      form.append("folder", sig.folder);
      const res = await fetch(`https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`, {
        method: "POST",
        body: form,
      });
      const uploaded = await res.json();
      if (!uploaded.secure_url) throw new Error(uploaded?.error?.message || "Upload failed");
      await api.post(`/clients/${clientId}/photos`, {
        cloudinary_public_id: uploaded.public_id,
        url: uploaded.secure_url,
        date,
        label: tag,
      });
      clearFile();
      onUploaded?.();
    } catch (e) {
      toast.error("Errore upload: " + (e.message || ""));
    } finally {
      setUploading(false);
    }
  };

  return (
    <form data-testid="photo-uploader" onSubmit={doUpload} className="border border-[color:var(--border)] rounded-2xl p-5 space-y-4 bg-[color:var(--bg-primary)]">
      <div>
        <Label className="label-tiny">Sorgente foto</Label>
        {/* Hidden inputs — one triggers device camera (mobile), one opens gallery/file picker */}
        <input
          ref={cameraInputRef}
          data-testid="photo-file-input-camera"
          type="file"
          accept="image/*"
          capture="environment"
          onChange={pickFile}
          className="hidden"
        />
        <input
          ref={galleryInputRef}
          data-testid="photo-file-input"
          type="file"
          accept="image/*"
          onChange={pickFile}
          className="hidden"
        />

        {!file ? (
          <div className="mt-1 grid grid-cols-2 gap-2" data-testid="photo-source-picker">
            <button
              type="button"
              data-testid="photo-take-camera-btn"
              onClick={() => cameraInputRef.current?.click()}
              className="flex flex-col items-center justify-center gap-1.5 py-4 rounded-xl border-2 border-[color:var(--border)] hover:border-[color:var(--text-primary)] transition-colors"
              style={{ background: "var(--bg-secondary)" }}
            >
              <Camera size={22} weight="duotone" style={{ color: "var(--accent)" }} />
              <span className="text-xs font-medium uppercase tracking-wider">Scatta foto</span>
            </button>
            <button
              type="button"
              data-testid="photo-pick-gallery-btn"
              onClick={() => galleryInputRef.current?.click()}
              className="flex flex-col items-center justify-center gap-1.5 py-4 rounded-xl border-2 border-[color:var(--border)] hover:border-[color:var(--text-primary)] transition-colors"
              style={{ background: "var(--bg-secondary)" }}
            >
              <Images size={22} weight="duotone" style={{ color: "var(--accent)" }} />
              <span className="text-xs font-medium uppercase tracking-wider">Galleria</span>
            </button>
          </div>
        ) : (
          <div
            data-testid="photo-preview"
            className="mt-1 flex items-center gap-3 border border-[color:var(--border)] rounded-xl p-2"
            style={{ background: "var(--bg-secondary)" }}
          >
            {preview && (
              <img src={preview} alt="anteprima" className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
            )}
            <div className="min-w-0 flex-1 text-xs">
              <div className="font-medium truncate">{file.name}</div>
              <div className="text-[color:var(--text-secondary)]">{(file.size / 1024).toFixed(0)} KB</div>
            </div>
            <button
              type="button"
              data-testid="photo-clear-btn"
              onClick={clearFile}
              className="w-8 h-8 inline-flex items-center justify-center rounded-full border border-[color:var(--border)] hover:border-red-300 hover:text-red-700 transition-colors flex-shrink-0"
              aria-label="Rimuovi file selezionato"
            >
              <CloseIcon size={14} weight="bold" />
            </button>
          </div>
        )}
      </div>
      <div>
        <Label className="label-tiny">Inquadratura</Label>
        <div className="mt-1 grid grid-cols-3 gap-2" data-testid="photo-tag-group">
          {TAGS.map((t) => {
            const active = tag === t.key;
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                type="button"
                data-testid={`photo-tag-${t.key}`}
                onClick={() => setTag(t.key)}
                className="flex flex-col items-center justify-center gap-1 py-3 rounded-xl border transition-all"
                style={{
                  background: active ? "var(--text-primary)" : "var(--bg-primary)",
                  color: active ? "var(--bg-primary)" : "var(--text-primary)",
                  borderColor: active ? "var(--text-primary)" : "var(--border)",
                }}
              >
                <Icon size={18} weight={active ? "fill" : "duotone"} />
                <span className="text-[11px] uppercase tracking-wider">{t.label}</span>
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <Label className="label-tiny">Data</Label>
        <SmartDateInput data-testid="photo-date" value={date} onChange={setDate} />
      </div>
      <Button data-testid="upload-photo-btn" type="submit" disabled={!file || uploading} className="w-full rounded-full bg-[color:var(--accent)] text-white hover:bg-[color:var(--accent-hover)] hover:text-white">
        <UploadSimple size={16} weight="bold" className="mr-2" />
        {uploading ? "Caricamento…" : "Carica foto"}
      </Button>
    </form>
  );
}
